// EarningsPage.jsx
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { ref, onValue, off, get } from 'firebase/database';
import { database } from './firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { AppContext } from './AppContext';
import cashdatImage from './assets/CASHOUTbutton.png';

/** ─────────────────────────────────────────────────────────────────────────────
 *  Web Earnings Page aligned with RN version
 *  - Tabs: Credits / Rewards
 *  - Available to cash out (observes configurable delay)
 *  - Max button, numeric input guard, unified payout CF function
 *  - Stripe onboarding helper (copies profile URL + opens portal)
 *  - Keeps original web styling approach
 *  ──────────────────────────────────────────────────────────────────────────── */

const EarningsPage = () => {
  const {
    currentUser,
    userId2,
    creditsEarned,           // legacy/earned credits balance
    datWallet,               // optional: token/rewards map (for Rewards cashout)
    setCurrentlyCashingOut,  // optional UI flag
  } = useContext(AppContext);

  const functions  = getFunctions();
  const firestore  = getFirestore();

  // UI state
  const [loadingStripeAccount, setLoadingStripeAccount] = useState(true);
  const [stripeAccountId, setStripeAccountId]           = useState(null);
  const [onboardingUrl, setOnboardingUrl]               = useState(null);

  const [activeTab, setActiveTab] = useState('cashout'); // kept for parity (single screen)
const [cashoutSource, setCashoutSource] = useState('credits'); // 'credits' | 'tokens'

  const [payoutAmount, setPayoutAmount] = useState('');
  const [daysDelay, setDaysDelay] = useState(4);
  const [availableToCashOutCredits, setAvailableToCashOutCredits] = useState(0);

  const [poolCents, setPoolCents]     = useState(0);
const [circulating, setCirculating] = useState(0);
const [userTokenQty, setUserTokenQty] = useState(0);


  // helpers
  const toNum = (n) => {
    if (n == null) return 0;
    if (typeof n === 'number') return Number.isFinite(n) ? n : 0;
    if (typeof n === 'string') return Number(n) || 0;
    if (typeof n === 'object') return toNum(Object.values(n)[0]);
    return 0;
  };
  const fmtUSD = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const spotUsd = useMemo(() => {
  const c = Number(circulating) || 0;
  const p = Number(poolCents) || 0;
  if (!c) return 0;
  return (p / 100) / c;   // poolCents is cents; convert to USD
}, [poolCents, circulating]);

const availableTokensUSD = useMemo(
  () => +(((Number(userTokenQty) || 0) * spotUsd).toFixed(2)),
  [userTokenQty, spotUsd]
);

// Credits are worth $0.10 each
const availableCreditsUSD = useMemo(
  () => +(((availableToCashOutCredits || 0) * 0.10).toFixed(2)),
  [availableToCashOutCredits]
);

const availableLabelUSD = cashoutSource === 'credits' ? availableCreditsUSD : availableTokensUSD;
const maxPayoutUSD      = cashoutSource === 'credits' ? availableCreditsUSD : availableTokensUSD;


  /* ── Stripe account fetch / onboarding link ─────────────────────────────── */
  useEffect(() => {
    const fetchStripeAccount = async () => {
      setLoadingStripeAccount(true);
      const createOrRetrieveAccount = httpsCallable(functions, 'createOrRetrieveAccount');
      try {
        const response = await createOrRetrieveAccount({ email: currentUser?.email });
        setStripeAccountId(response.data?.stripeAccountId || null);

        if (!response.data?.payouts_enabled) {
          // Use web-friendly onboarding function
          const onboardingLink = httpsCallable(functions, 'createStripeOnboardingLink');
          const linkResponse   = await onboardingLink();
          setOnboardingUrl(linkResponse.data?.url || null);
        } else {
          setOnboardingUrl(null);
        }
      } catch (error) {
        console.error('Error fetching Stripe account:', error);
      } finally {
        setLoadingStripeAccount(false);
      }
    };
    fetchStripeAccount();
  }, [currentUser?.email, functions]);

  useEffect(() => {
  if (!userId2) return;

  const poolRef = ref(database, 'metrics/datToken/poolCents');
  const circRef = ref(database, 'metrics/datToken/circulating');
  const qtyRef  = ref(database, `datChainTokenWallets/${userId2}/qty`);

  const unsubPool = onValue(poolRef, s => setPoolCents(Number(s.val() || 0)), () => setPoolCents(0));
  const unsubCirc = onValue(circRef, s => setCirculating(Number(s.val() || 0)), () => setCirculating(0));
  const unsubQty  = onValue(qtyRef,  s => setUserTokenQty(Number(s.val() || 0)), () => setUserTokenQty(0));

  return () => {
    off(poolRef, 'value', unsubPool);
    off(circRef, 'value', unsubCirc);
    off(qtyRef,  'value', unsubQty);
  };
}, [userId2]);


useEffect(() => {
  const loadCashable = async () => {
    // 1) read delay value from Firestore
    let delay = 4;
    try {
      const cfgSnap = await getDoc(doc(firestore, 'TimeToPayout', 'config'));
      if (cfgSnap.exists()) delay = cfgSnap.data().days;
    } catch {}
    setDaysDelay(delay);

    // 2) cutoff date (YYYY-MM-DD)
    const cutoff = new Date(Date.now() - delay * 86_400_000).toISOString().slice(0, 10);

    // 3) read daily earned/ spent summary from RTDB
    const dailySnap = await get(ref(database, `users/${userId2}/summary/earnedCreditsByDate`));
    const dailyVal  = dailySnap.val() || {};

    let agedEligible = 0;
    Object.entries(dailyVal).forEach(([date, rec]) => {
      if (date <= cutoff) {
        const total = parseFloat(rec.total || '0');
        const spent = parseFloat(rec.spent || '0');
        agedEligible += Math.max(total - spent, 0);
      }
    });

    // 4) clamp to owned credits
    const owned = Number(creditsEarned) || 0;
    setAvailableToCashOutCredits(Math.max(0, Math.min(agedEligible, owned)));
  };

  if (userId2) loadCashable();
}, [userId2, creditsEarned]);


  /* ── Cash Out handler (unified) ────────────────────────────────────────── */
  const handleCashOut = async () => {
    const minUSD = 25;
    const amountUSD = parseFloat(payoutAmount) || 0;

    if (Number.isNaN(amountUSD) || amountUSD < minUSD) {
      alert(`Minimum payout amount is $${minUSD}.`);
      return;
    }
    if (amountUSD > maxPayoutUSD) {
      setPayoutAmount(String(maxPayoutUSD));
      return;
    }

    try {
      setCurrentlyCashingOut?.(true);
const processStripePayout = httpsCallable(functions, 'processStripePayoutDatChainApps2');
await processStripePayout({ payoutAmount: amountUSD, source: cashoutSource });
      alert('Payout successful!');
      setPayoutAmount('');
    } catch (error) {
      console.error('Error processing payout:', error);
      alert(error?.message || 'Failed to process payout. Please try again.');
    } finally {
      setCurrentlyCashingOut?.(false);
    }
  };

  /* ── Loading / Onboarding UIs ──────────────────────────────────────────── */
  if (loadingStripeAccount) {
    return (
      <div style={styles.fullScreen}>
        <div style={styles.emailPromptContainer}>
          <h2 style={styles.promptTitle}>Loading...</h2>
        </div>
      </div>
    );
  }

  if (onboardingUrl) {
    const copyToClipboard = () => {
      const link = `spndat.com/userprofile/${currentUser?.username}`;
      navigator.clipboard.writeText(link)
        .then(() => alert('Link copied to clipboard!'))
        .catch(err => console.error('Failed to copy: ', err));
    };

    return (
      <div style={styles.fullScreen}>
        <div style={styles.emailPromptContainer}>
          <h2 style={styles.promptTitle}>Complete Your Stripe Account Setup</h2>
          <p style={styles.promptTitle}>
            Enter <strong>spndat.com/userprofile/{currentUser?.username}</strong> in the "Your website" field
          </p>
          <button style={styles.copyButton} onClick={copyToClipboard}>Click to Copy</button>
          <button style={styles.submitButton} onClick={() => (window.location.href = onboardingUrl)}>
            Complete Setup
          </button>
        </div>
      </div>
    );
  }

  /* ── Main UI ───────────────────────────────────────────────────────────── */
  return (
    <div style={styles.fullScreen}>
      <div style={styles.scrollContainer}>
        {/* Title */}
        <div style={styles.EarningsPopup}>
          <h2 style={styles.titleText}>Earnings</h2>

          {/* Tabs: Credits / Rewards */}
<div style={styles.tabsRow}>
  <button
    onClick={() => setCashoutSource('credits')}
    style={{ ...styles.tabButton, ...(cashoutSource === 'credits' ? styles.tabButtonActive : null) }}
  >
    Credits
  </button>
  <button
    onClick={() => setCashoutSource('tokens')}
    style={{ ...styles.tabButton, ...(cashoutSource === 'tokens' ? styles.tabButtonActive : null) }}
  >
    DAT Chain Tokens
  </button>
</div>


          {/* Available to Cashout */}
          <h3 style={styles.availableLabel}>Available To Cashout</h3>
          <h3 style={styles.valueTextCB}>${fmtUSD(availableLabelUSD)}</h3>

          {/* Amount input + Max */}
          <input
            type="number"
            inputMode="decimal"
            placeholder="Enter payout amount"
            value={payoutAmount}
            onChange={(e) => {
              const raw = e.target.value.replace(/[^0-9.]/g, '').replace(/^0+(?!\.|$)/, '');
              const val = parseFloat(raw) || 0;
              const capped = Math.min(val, maxPayoutUSD);
              setPayoutAmount(String(capped));
            }}
            style={styles.inputField}
          />
          <button
            style={styles.maxButton}
            onClick={() => setPayoutAmount(String(maxPayoutUSD))}
          >
            Max
          </button>

          {/* Cash out image button */}
          <img
            src={cashdatImage}
            alt="Cash Out"
            onClick={handleCashOut}
            style={styles.fullScreenImage3}
          />
        </div>
      </div>
    </div>
  );
};

/* ───────────────────────── styles (web) ───────────────────────── */
const styles = {
  fullScreen: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    overflow: 'hidden',
    position: 'relative',
    justifyContent: 'center',
    alignSelf: 'center',
    fontFamily: 'brhendrix',
  },
  scrollContainer: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '24px 20px 40px',
    position: 'relative',
    justifyContent: 'center',
  },

  /* Main card area kept simple and centered */
  EarningsPopup: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignSelf: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    width: '100%',
    maxWidth: 680,
    minHeight: '52vh',
    zIndex: 2,
  },

  titleText: {
    fontSize: 28,
    color: '#62fbfa',
    marginBottom: 8,
    fontWeight: '800',
    textAlign: 'center',
  },

  /* Tabs (web) */
  tabsRow: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    justifyContent: 'center',
    margin: '6px 0 14px',
  },
  tabButton: {
    background: 'transparent',
    color: 'white',
    borderRadius: 6,
    padding: '8px 18px',
    border: '1px solid transparent',
    cursor: 'pointer',
    fontWeight: 700,
    letterSpacing: 0.2,
  },
  tabButtonActive: {
    background: 'rgba(98,251,250,0.13)',
    border: '1px solid #62fbfa',
  },

  availableLabel: {
    fontSize: 18,
    color: '#62fbfa',
    fontWeight: '700',
    margin: '6px 0 4px',
    textAlign: 'center',
  },
  valueTextCB: {
    fontSize: 26,
    color: '#62fbfa',
    fontWeight: '800',
    textAlign: 'center',
    marginBottom: 14,
  },

  inputField: {
    width: '60%',
    minWidth: 260,
    maxWidth: 420,
    height: 42,
    border: '1px solid #6b92c2',
    backgroundColor: 'transparent',
    color: 'white',
    padding: '10px 12px',
    borderRadius: 6,
    fontSize: 16,
    outline: 'none',
    marginBottom: 10,
  },

  maxButton: {
    alignSelf: 'center',
    width: 140,
    height: 40,
    marginBottom: 18,
    backgroundColor: 'transparent',
    color: '#62fbfa',
    fontWeight: 800,
    fontSize: 16,
    border: '1px solid transparent',
    cursor: 'pointer',
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  fullScreenImage3: {
    display: 'flex',
    width: 180,
    height: 'auto',
    position: 'relative',
    alignSelf: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
    cursor: 'pointer',
    zIndex: 1,
  },

  /* Onboarding / loading blocks (kept from original) */
  emailPromptContainer: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
    zIndex: 1,
    minHeight: '30vh',
    position: 'relative',
  },
  promptTitle: {
    fontSize: 16,
    color: 'white',
    fontWeight: 'bold',
    textAlign: 'center',
    padding: '12px',
    marginBottom: '14px',
    fontFamily: 'brhendrix',
  },
  copyButton: {
    fontSize: 14,
    backgroundColor: '#f1f1f1',
    color: '#333',
    padding: '10px 20px',
    borderRadius: 5,
    border: '1px solid #ccc',
    cursor: 'pointer',
    marginBottom: 10,
  },
  submitButton: {
    padding: '10px 20px',
    borderRadius: 5,
    alignItems: 'center',
    backgroundColor: 'transparent',
    color: '#62fbfa',
    fontSize: 18,
    fontWeight: 'bold',
    border: 'none',
    cursor: 'pointer',
  },
};

export default EarningsPage;
